import { applyImportedTableConfig, type TableConfig } from "echart-table-library";
import type { WidgetToolbarConfig } from "@/components/Widget/WidgetToolbar";
import { BASIC_INFO_PROGRESS_COLORS } from "@/shared/constants/chartColors";
import type { SortRule } from "@/shared/types";
import importedTableConfig from "./table-config.json";

/**
 * Default server-side sort: email ascending. Shared by the table's initial
 * sorting state (header arrow) and the first page fetch so the mock service
 * returns rows already in the table's displayed order.
 */
export const DEFAULT_SORT: SortRule[] = [{ id: "email", desc: false }];

/**
 * Base widget config: display-independent behavior only (headers, alignment,
 * business `$callback` renderers, selection identity). Presentation metadata
 * — display types, widths, hide flags, pinning, row height, virtualization —
 * comes from the imported `table-config.json` below, folded in with the
 * library's import adapter. All loaded rows render (virtualized); paging is
 * the library's load-more mechanism, not a pager.
 */
const baseConfig: TableConfig = {
  idField: "id",
  currency: "USD",
  locale: "en-US",
  selection: { enabled: true, mode: "multi", idField: "id" },
  columns: [
    // Checkbox cell rendered by a registered business callback; the import
    // pins this column left.
    { id: "select", header: "", cellRenderer: { $callback: "renderSelectCheckbox" } },
    { id: "email", header: "Email" },
    { id: "fullName", header: "Full Name" },
    { id: "status", header: "Status", cellRenderer: { $callback: "renderBasicInformationStatusCell" } },
    { id: "role", header: "Role" },
    { id: "department", header: "Department" },
    // Footer aggregates: additive measures sum over the loaded rows;
    // percentage columns average (a sum of percentages is meaningless).
    // Progress-cell accents come from the central color registry (colors
    // are app-managed, not interchange data).
    { id: "salary", header: "Salary", align: "right", aggregate: "sum" },
    { id: "quantity", header: "Quantity", align: "right", aggregate: "sum" },
    { id: "timeSpent", header: "Time Spent", align: "right", aggregate: "sum" },
    { id: "createdAt", header: "Created" },
    { id: "progress", header: "Progress", aggregate: "avg" },
    { id: "capacity", header: "Capacity", aggregate: "avg", color: BASIC_INFO_PROGRESS_COLORS.capacity },
    { id: "completion", header: "Completion", aggregate: "avg", color: BASIC_INFO_PROGRESS_COLORS.completion },
  ],
};

/**
 * The imported interchange config (same shape the reference project's
 * settings panel exports): `columnOrder` (62 columns incl. dynamic_1..49),
 * left pinning for select/email, rowHeight 40, footer on and
 * virtualization enabled. Unknown display types fall back to `text`; base
 * headers and `$callback` renderers survive the fold by column id.
 */
export const tableConfig: TableConfig = {
  ...applyImportedTableConfig(baseConfig, importedTableConfig),
  // Server-side sorting (section 52): header clicks delegate upward via
  // `onSortingChange` and the widget refetches page 0 with the sort param —
  // the table never sorts rows itself. Defaults to email ascending.
  sorting: { enabled: true, manual: true, initial: DEFAULT_SORT },
  // Footer totals row: `showFooter` comes from the imported config; the
  // label sits in the pinned email column so it stays visible while
  // scrolling horizontally.
  footerTotals: { enabled: true, labelColumnId: "email" },
  // Filter button + modal over the loaded rows (advanced-table-demo parity):
  // the library's toolbar renders the trigger and applies rules client-side.
  filtering: true,
  // Load-more on scroll is the table's paging mechanism (no pager): a
  // near-bottom scroll fires `onScrollNearBottom` and the widget dispatches
  // the next-page thunk.
  infiniteScroll: { enabled: true },
};

/** Toolbar capability declaration (section 23): what this widget supports. */
export const toolbarConfig: WidgetToolbarConfig = {
  exportCsv: true,
};
